import React, { ReactNode } from 'react'
import { ComponentVariant, Vehicle, WidgetType } from '@otw/models'
import { DashboardWidget } from './DashboardWidget'
import {
  useDashboardSoldVehicles,
  useDashboardInventoryVehicles,
  useDashboardWidgetQuery,
  type DashboardDateRange,
} from 'hooks/dashboard'

interface DashboardSoldWidgetProps {
  widgetId: WidgetType
  dateRange: DashboardDateRange
  componentVariant?: ComponentVariant
  loadingHeight?: number
  label?: string
  children: (vehicles: Vehicle[]) => ReactNode
}

export const DashboardSoldWidget: React.FC<DashboardSoldWidgetProps> = ({
  widgetId,
  dateRange,
  componentVariant,
  loadingHeight,
  label,
  children,
}) => {
  const { vehicles, isLoading } = useDashboardSoldVehicles(dateRange, widgetId)
  return (
    <DashboardWidget
      widgetId={widgetId}
      componentVariant={componentVariant}
      isLoading={isLoading}
      loadingHeight={loadingHeight}
      label={label}
    >
      {children(vehicles)}
    </DashboardWidget>
  )
}

interface DashboardInventoryWidgetProps {
  widgetId: WidgetType
  dateRange: DashboardDateRange
  componentVariant?: ComponentVariant
  loadingHeight?: number
  label?: string
  children: (vehicles: Vehicle[], allVehicles: Vehicle[]) => ReactNode
}

export const DashboardInventoryWidget: React.FC<DashboardInventoryWidgetProps> = ({
  widgetId,
  dateRange,
  componentVariant,
  loadingHeight,
  label,
  children,
}) => {
  const { vehicles, allVehicles, isLoading } = useDashboardInventoryVehicles(dateRange, widgetId)
  return (
    <DashboardWidget
      widgetId={widgetId}
      componentVariant={componentVariant}
      isLoading={isLoading}
      loadingHeight={loadingHeight}
      label={label}
    >
      {children(vehicles, allVehicles)}
    </DashboardWidget>
  )
}

interface DashboardVehicleStatProps<T> {
  widgetId: WidgetType
  dateRange?: DashboardDateRange
  extra?: unknown
  loadingHeight?: number
  label?: string
  scope?: 'sold' | 'active'
  select: (vehicles: Vehicle[], dateRange?: DashboardDateRange) => T
  children: (data: T) => ReactNode
}

export function DashboardVehicleStat<T>({
  widgetId,
  dateRange,
  extra,
  loadingHeight = 96,
  label,
  scope,
  select,
  children,
}: DashboardVehicleStatProps<T>) {
  const { data, isLoading } = useDashboardWidgetQuery(widgetId, select, dateRange, extra, scope)
  const resolved = data !== undefined ? data : select([], dateRange)
  return (
    <DashboardWidget widgetId={widgetId} isLoading={isLoading} loadingHeight={loadingHeight} label={label}>
      {children(resolved)}
    </DashboardWidget>
  )
}
